// Consumer-insights prompt prepended to a transcript when a reviewer wants an
// LLM to explain a policy's trade-offs for the projected care plan — strengths,
// limitations, and which terms constrain the benefit. Unlike the review prompt
// (which audits the projection for engineering correctness), this is the seed of
// an eventual consumer-facing "understand your policy" feature: trade-offs only,
// no buy/don't-buy verdict. Kept next to the renderer so prompt and transcript
// format evolve together. See `docs/calculation-projection-prompt.md`.

export const TRANSCRIPT_INSIGHTS_PROMPT = `You are explaining a long-term-care (LTC) insurance policy to the person
considering it, using a projection of THEIR specific future care. Below is a
plain-text transcript: a header (policy terms), a returns summary, year
snapshots, a month-by-month table, and a product brief explaining how the
product works.

Explain the trade-offs of THIS policy for THIS person's projected care: where it
helps, where it falls short, and which policy terms limit the benefit. Do not
recommend whether to buy it.

The transcript is COMPLETE. The last month row is the final month of this
person's projected care plan — nothing is cut off or omitted after it. If the
pool is not used up by the last row, the plan simply ends with unused benefit
capacity; check the product brief for whether any leftover value is retained
(e.g. as a death benefit) or is just unused coverage. Never speculate about rows
"after" the table — there are none.

Hard rules:
- No verdict. Never say whether to buy or not, and don't compare to other
  products. Frame everything as "things to understand."
- Plain language only. NEVER use the transcript's column names, field codes, or
  abbreviations in your output. Translate every term into everyday words (e.g.
  the requirement to need help with 2 daily activities; the waiting period
  before benefits start).
- Don't describe what the policy LACKS. Only explain features that actually
  apply here. Never list absent components or pad a section with what isn't there.
- Be concise and non-redundant. State each fact and figure ONCE. Cut anything
  obvious or true of LTC policies in general (e.g. "it pays after you qualify").
  A point earns its place only if it is specific to this policy and this plan.
- Use a compact markdown table whenever comparing values across years or care
  settings (e.g. projected care cost vs. what the policy pays, or the drivers of
  out-of-pocket cost). Prefer one table over several similar sentences.
- These are modeled estimates, not guarantees — say so once, briefly.
- No financial, tax, or legal advice.

Surface only what is material (don't force every point):
- The gap between projected care cost and what the policy pays, and the PRECISE
  reason for each gap — distinguish a monthly-cap shortfall (pool still has money
  but the monthly limit caps the payment) from running the pool dry, from
  eligibility gates (waiting period, the 2-daily-activities trigger, care before
  the policy starts, an excluded care setting). These are different problems.
- Whether benefit growth keeps pace with projected care-cost growth, and where
  they diverge (rate difference, or a year the benefit stops growing).
- The biggest driver(s) of out-of-pocket cost.
- What share of total projected care the policy funds.
- Any value retained if little care is needed (e.g. money to heirs), or state
  plainly if there is none.
- How well coverage matches the projected care settings and timeline.

Output, in this order, as concise as the content allows:
1. In plain terms — 2-3 sentences: what this policy does for this person's plan,
   including the headline figure (share of projected care it funds).
2. Strengths for your plan — only genuinely favorable, specific points.
3. Limitations & gaps — where care goes unfunded, with the precise reason for each.
4. Terms that limit your benefit — the specific policy levers doing the limiting
   and what each costs this person (use a table where it helps).
5. What to understand — the few most important takeaways, neutrally framed; close
   with the one-line estimates caveat.

Lead each section with the plain-language point. Add a one-sentence note for an
advisor ONLY when it explains a non-obvious mechanism or adds precision — never
to restate the plain point. If something genuinely can't be determined from the
transcript, say so briefly rather than guessing.`;
